import { z } from "zod";
import { extendZodWithOpenApi, type ZodOpenApiPathsObject } from "zod-openapi";
import { privateApi, successResponse } from "../util.ts";
import { editableFields } from "./billing.editableFields.ts";
import { LinkProcessorId } from "./billing.ts";
import { broken_boolean, int } from "./common.ts";
import { Location } from "./locations.ts";

extendZodWithOpenApi(z);

//#region VM config
const VmSizeOs = z.enum(["win", "other"]).openapi({ ref: "VmSizeOs" });

const VmSize = z
    .object({
        cpu: int,
        ram: z.number(),
        ssd: z.number(),
        default: z.array(VmSizeOs),
        disabled: z.array(VmSizeOs).optional(),
        os: z.array(VmSizeOs),
        notice: z.string().optional(),
    })
    .refine((v) => !v.disabled?.some((os) => !v.os.includes(os)), {
        message: "Disabled OS must be in the list of OS",
    })
    .refine((v) => !v.default.some((os) => !v.os.includes(os)), {
        message: "Default OS must be in the list of OS",
    })
    .refine((v) => !v.default.some((os) => v.disabled?.includes(os)), {
        message: "OS cannot be default if it is disabled",
    })
    .openapi({ ref: "VmSize" });
//#endregion

//#region Chat config
const IntercomConfig = z
    .object({
        type: z.literal("intercom"),
        intercom_app_id: z.string(),
    })
    .openapi({ ref: "IntercomConfig" });

const QiscusConfig = z
    .object({
        type: z.literal("qiscus"),
        qiscus_client_key: z.string(),
        qiscus_channel_id: z.string(),
    })
    .openapi({ ref: "QiscusConfig" });

const BotikaConfig = z
    .object({
        type: z.literal("botika"),
        botika_client_key: z.string(),
    })
    .openapi({ ref: "BotikaConfig" });

const OncehubConfig = z
    .object({
        type: z.literal("oncehub"),
        oncehub_website_id: z.string(),
        oncehub_bot_id: z.string(),
    })
    .openapi({ ref: "OncehubConfig" });

const LeadBoosterConfig = z
    .object({
        type: z.literal("leadbooster"),
        company_id: z.string(),
        playbook_uuid: z.string(),
    })
    .openapi({ ref: "LeadBoosterConfig" });

const TawkConfig = z
    .object({
        type: z.literal("tawk"),
        widget_id: z.string(),
        property_id: z.string(),
    })
    .openapi({ ref: "TawkConfig" });

const JivochatConfig = z
    .object({
        type: z.literal("jivochat"),
        widget_id: z.string(),
    })
    .openapi({ ref: "JivochatConfig" });

const ChatConfig = z
    .union([
        z.discriminatedUnion("type", [
            IntercomConfig,
            QiscusConfig,
            BotikaConfig,
            OncehubConfig,
            LeadBoosterConfig,
            TawkConfig,
            JivochatConfig,
        ]),
        // Empty object currently if disabled, perhaps should be `null`...
        z.object({}),
    ])
    .openapi({ ref: "ChatConfig" });
//#endregion

//#region Payment config
const ProcessorConfigBase = z
    .object({
        type: z.string(),
        keyword: z.string(),
        name: z.string(),
        disable_add: broken_boolean.optional(),
    })
    // NB: Allow unknown fields in processor configs for forwards compatibility
    .passthrough()
    .openapi({ ref: "ProcessorConfigBase" });

const ProcessorServiceFee = z
    .object({
        flat_rate: z.number().optional(),
        percentage: z.number().min(0).max(100).optional(),
    })
    .openapi({ ref: "ProcessorServiceFee" });

const ProcessorCurrencyFee = z
    .object({
        to_currency: z.string().length(3),
        for_country: z
            .string()
            .min(2)
            .optional()
            .describe("If set, the exchange rate will be applied only to the specified country."),
        exchange_rate: z.unknown().describe("[DEPRECATED]"),
    })
    .openapi({ ref: "ProcessorCurrencyFee" });

const ProcessorFeeConfig = z
    .object({
        service_fee: ProcessorServiceFee.optional(),
        charge_currency_conversion: ProcessorCurrencyFee.optional(),
    })
    .openapi({ ref: "ProcessorFeeConfig" });

const CardConfigBase = ProcessorConfigBase.extend(ProcessorFeeConfig.shape)
    .extend({
        type: z.literal("creditcard"),
        public_key: z.string(),
        verification_amount: z.number().optional(),
        supports_auto_topup: z.boolean().optional().describe("If missing, true is assumed."),
    })
    .openapi({ ref: "CardConfigBase" });

const StripeCardConfig = CardConfigBase.extend({
    keyword: z.literal("stripe_creditcard"),

    service_fee: ProcessorServiceFee.optional(),

    show_postalcode_input: broken_boolean.optional(),
}).openapi({ ref: "StripeCardConfig" });

const OmiseCreditCardConfig = CardConfigBase.extend({
    keyword: z.literal("omise_creditcard"),

    service_fee: ProcessorServiceFee.optional(),

    use_3ds_verification: broken_boolean.optional(),
}).openapi({ ref: "OmiseCreditCardConfig" });

const PaypalVaultConfig = CardConfigBase.extend({
    keyword: z.literal("paypal_vault"),
    public_key: z.undefined(),
    client_id: z.string(),
}).openapi({ ref: "PaypalVaultConfig" });

const MethodConfig = z
    .object({
        key: z.string(),
        name: z.string(),
    })
    .openapi({ ref: "MethodConfig" });

const HybridConfig = ProcessorConfigBase.extend({
    type: z.literal("hybrid"),
    keyword: z.literal("adyen"),

    methods: z.array(MethodConfig),
}).openapi({ ref: "HybridConfig" });

const InvoiceConfig = ProcessorConfigBase.extend({
    type: z.literal("invoice"),
    keyword: z.literal("invoice"),
    default_allow_invoice: z.boolean().optional(),
}).openapi({ ref: "InvoiceConfig" });

const LinkConfigBase = ProcessorConfigBase.extend(ProcessorFeeConfig.shape)
    .extend({
        type: z.literal("link"),
        keyword: LinkProcessorId,
        methods: z.array(MethodConfig),
    })
    .openapi({ ref: "LinkConfigBase" });

const DuitkuConfig = LinkConfigBase.extend({
    keyword: z.literal("duitku"),

    public_key: z.string(),
}).openapi({ ref: "DuitkuConfig" });

const StripeWalletConfig = LinkConfigBase.extend({
    keyword: z.literal("stripe_wallet"),

    public_key: z.string(),
}).openapi({ ref: "StripeWalletConfig" });

const PaypalCheckoutConfig = LinkConfigBase.extend({
    keyword: z.literal("paypal_checkout"),

    client_id: z.string(),
}).openapi({ ref: "PaypalCheckoutConfig" });

const OmiseConfig = LinkConfigBase.extend({
    keyword: z.literal("omise"),

    public_key: z.string(),
}).openapi({ ref: "OmiseConfig" });

const SslcommerzConfig = LinkConfigBase.extend({
    keyword: z.literal("sslcommerz"),

    store_id: z.string(),

    is_test: z.unknown().optional(),
    accept_high_risk: z.unknown().optional(),
}).openapi({ ref: "SslcommerzConfig" });

const RazorpayConfig = LinkConfigBase.extend({
    keyword: z.literal("razorpay"),
    key_id: z.string(),
}).openapi({ ref: "RazorpayConfig" });

const ClicToPayConfig = LinkConfigBase.extend({
    keyword: z.literal("clictopay"),

    username: z.string(),
    is_test: z.unknown().optional(),
}).openapi({ ref: "ClicToPayConfig" });

const CoingateConfig = LinkConfigBase.extend({
    keyword: z.literal("coingate"),
    is_test: z.unknown().optional(),
    receive_currency: z.string().optional(),
}).openapi({ ref: "CoingateConfig" });

const EvcHormuudConfig = LinkConfigBase.extend({
    keyword: z.literal("evc_hormuud"),
}).openapi({ ref: "EvcHormuudConfig" });

const ProcessorConfig = z
    .discriminatedUnion("keyword", [
        StripeCardConfig,
        OmiseCreditCardConfig,
        PaypalVaultConfig,
        SslcommerzConfig,
        RazorpayConfig,
        ClicToPayConfig,
        CoingateConfig,
        EvcHormuudConfig,
        StripeWalletConfig,
        PaypalCheckoutConfig,
        DuitkuConfig,
        OmiseConfig,
        InvoiceConfig,
        HybridConfig,
    ])
    .openapi({ ref: "ProcessorConfig" });

const ProcessorConfigArray = z
    .array(
        // TODO: Make this better once the OpenAPI-to-Typescript generator supports `union` with a `discriminatedUnion`

        // Forward compatibility: Allow unknown configuration types (and arbitrary errors, unfortunately)
        ProcessorConfig.catch((e) => {
            console.warn("ProcessorConfig error: %o", e.error);
            // Pass through the original input (no Zod error will be reported to the top-level caller)
            return e.input;
        }),
    )
    .openapi({ ref: "ProcessorConfigArray" });
//#endregion

//#region Billing
const EditableFieldName = z.enum(editableFields).openapi({ ref: "EditableFieldName" });

const BillingAccountField = z
    .object({
        field: EditableFieldName,
        required: z.boolean(),
        visible: z.boolean().optional(),
        validator: z
            .object({
                type: z.string(),
                group: z.string().optional(),
            })
            .optional(),
    })
    .openapi({ ref: "BillingAccountField" });
//#endregion

export const CurrencyCode = z
    .string()
    .length(3)
    .describe("Currency code, eg. EUR, USD")
    .openapi({ ref: "CurrencyCode" });

export const DisplayCurrency = z
    .object({
        code: CurrencyCode,
        title: z.string().describe("Label shown in the UI for the currency."),
        rate: z.number().min(0).describe("Exchange rate relative to the site base currency."),
    })
    .openapi({ ref: "DisplayCurrency" });

const AccountValidationSimple = z
    .object({
        process: z.enum(["NO_VALIDATION", "MANUAL"]),
    })
    .openapi({ ref: "AccountValidationSimple" });

const AccountValidationExternalDigio = z
    .object({
        process: z.literal("EXTERNAL"),
        provider: z.literal("digio"),
        test_mode: z.number().optional(),
        provider_conf: z.object({
            client_id: z.string(),
            workflow_name: z.string(),
            workflow_name_by_country: z
                .record(
                    z.string().length(2).toLowerCase(), // keys: country iso2 codes
                    z.string(), // values: any string
                )
                .optional(),
            notify_customer: z.number().optional(),
            auto_accept: z.number().optional(),
            whitelist_ips: z.array(z.string()).optional(),
        }),
    })
    .openapi({ ref: "AccountValidationExternalDigio" });

export const MainConfig = z
    .object({
        // !!!!!!! IMPORTANT !!!!!!!
        // NB: All new/added fields MUST always be `.optional()` for backwards compatibility.
        // (and make them `.nullable()` if they are actually optional)
        // Also add them to `defaultConfig` in the frontend.
        // !!!!!!! IMPORTANT !!!!!!!

        siteName: z.string(),
        siteTheme: z.string(),
        siteCountry: z.string().length(2),
        siteCurrency: CurrencyCode,
        siteLocale: z.string(),

        apiDocUrl: z.string(),
        apiGrafanaUrl: z.string().optional().nullable(),

        termsUrl: z
            .object({
                url: z.string().optional(),
                url_text: z.string().optional(),
            })
            .optional(),

        // XXX(WRN-318): Make this non-optional
        locations: z.array(Location).optional(),

        // Branding (move to theme?)
        loginTitle: z.string().optional(),
        loginSubtitle: z.string().optional(),

        // Enabled features
        vmsEnabled: z.boolean().optional(),
        vmAppsEnabled: z.boolean(),
        ipsEnabled: z.boolean().optional(),
        privateNetworksEnabled: z.boolean(),
        loadBalancerEnabled: z.boolean(),
        storageEnabled: z.boolean(),
        servicesEnabled: z.boolean(),
        kubernetesEnabled: z.boolean(),
        bareMetalEnabled: z.boolean().optional(),
        firewallEnabled: z.boolean().optional(),
        disksEnabled: z.boolean().optional(),

        // Login & auth
        auth0: z.object({
            domain: z.string(),
            clientID: z.string(),
        }),
        mfaAvailable: z.boolean(),

        // Promotions
        recurringPaymentsEnabled: z.boolean(),
        firstAccountCredit: z.number(),
        referralCodeEnabled: z.boolean(),
        referralCreditAmounts: z.object({
            receiver_credit: z.number(),
            promoter_credit: z.number(),
            referral_credit_receiver_precondition_topup_sum: z.number().optional().openapi({ deprecated: true }),
            receiver_precondition_topup_sum: z.number().optional(),
        }),

        // Payment
        billingEmail: z.string().email(),
        limitedBillingAccountConfig: z.object({
            vcpu: z.number(),
            ram_mb: z.number(),
        }),
        minimumTopupAmount: z.number(),
        allowDebt: z.boolean(),
        paymentProcessors: ProcessorConfigArray,
        billingAccountFields: z.array(BillingAccountField),

        billingAccountValidation: z.union([AccountValidationSimple, AccountValidationExternalDigio]).optional(),

        // Misc
        predefinedPackages: z.array(VmSize),
        backupTime: z.string().nullable().optional(),
        s3ForcePathStyle: z.boolean(),

        // Support
        supportEmail: z.string().email(),
        supportUrl: z.union([z.string().url(), z.literal("")]),
        chatConfig: ChatConfig,

        displayCurrencies: z.array(DisplayCurrency).nullable().optional(),

        billingAccountValidationActive: z.boolean().optional(),

        // XXX: Figure out `null` vs `""` usage here
        google_tag_manager_key: z
            .string()
            .regex(/GTM-[A-Z0-9-]+|^$/)
            .nullable()
            .optional(),
        google_analytics_key: z
            .string()
            .regex(/G-[A-Z0-9-]+|^$/)
            .nullable()
            .optional(),

        // !!!!!!! IMPORTANT !!!!!!!
        // ^^^^^^^ Read the comment above ^^^^^^^^
    })
    .passthrough()
    .refine((v) => !v.kubernetesEnabled || v.servicesEnabled, {
        message: "Kubernetes requires services to be enabled",
    })
    .openapi({ ref: "MainConfig" });

export const configPaths: ZodOpenApiPathsObject = {
    "/config/ui/config": {
        get: {
            summary: `Get UI config ${privateApi}`,
            tags: ["config"],
            responses: {
                ...successResponse(MainConfig),
            },
        },
    },
};
